'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { ArrowRight, Heart, Users, Cog, Luggage } from 'lucide-react';
import { Vehicle } from '@/types';
import { useCurrency } from '@/components/site/CurrencyContext';
import { useLocale } from '@/lib/i18n';
import { CarIllustration } from './CarIllustrations';

interface PopularVehiclesProps {
  vehicles: Vehicle[];
  favorites: string[];
  onToggleFavorite: (id: string) => void;
  onBookCar: (vehicle: Vehicle) => void;
  onViewAll: () => void;
  onViewDetails: (vehicle: Vehicle) => void;
}

export function PopularVehicles({
  vehicles,
  favorites,
  onToggleFavorite,
  onBookCar,
  onViewAll,
  onViewDetails,
}: PopularVehiclesProps) {
  const { fmt, equiv } = useCurrency();
  const { t } = useLocale();
  // Take top 4 highlighted vehicles matching the reference image
  const popularList = vehicles.slice(0, 4);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const handleImageError = (id: string) => {
    setImageErrors((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 lg:py-14">
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <span className="text-xs font-black tracking-wider uppercase text-[var(--color-primary)] block mb-1">
            {t('pop.kicker')}
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            {t('pop.title')}
          </h2>
        </div>

        <button
          type="button"
          onClick={onViewAll}
          className="group inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] transition-colors cursor-pointer"
        >
          <span>{t('pop.viewAll')}</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* 4 Car Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {popularList.map((car) => {
          const isFav = favorites.includes(car.id);
          const hasError = imageErrors[car.id] || !car.imageUrl;

          return (
            <div
              key={car.id}
              className="bg-white rounded-2xl border border-slate-200/90 hover:border-slate-300 p-4 shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col justify-between group cursor-pointer"
              onClick={() => onViewDetails(car)}
            >
              <div>
                {/* Top: Wishlist Favorite Button */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    {car.category}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavorite(car.id);
                    }}
                    className="p-1.5 rounded-full hover:bg-slate-100 transition-colors cursor-pointer text-slate-400 hover:text-rose-500"
                    aria-label="Add to favorites"
                  >
                    <Heart
                      className={`w-4 h-4 transition-colors ${
                        isFav ? 'fill-rose-500 text-rose-500' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Car Photographic Image / Fallback Container */}
                <div className="relative w-full h-40 flex items-center justify-center p-2 rounded-xl bg-slate-50 group-hover:bg-slate-100/70 transition-colors overflow-hidden">
                  {!hasError && car.imageUrl ? (
                    <div className="relative w-full h-full">
                      <Image
                        src={car.imageUrl}
                        alt={car.name}
                        fill
                        className="object-cover rounded-lg group-hover:scale-105 transition-transform duration-500"
                        referrerPolicy="no-referrer"
                        onError={() => handleImageError(car.id)}
                      />
                    </div>
                  ) : (
                    <CarIllustration
                      type={car.imageType}
                      className="w-full h-auto max-h-32 object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                </div>

                {/* Car Details */}
                <div className="mt-4">
                  <h3 className="text-base font-extrabold text-slate-900 tracking-tight group-hover:text-[var(--color-primary)] transition-colors">
                    {car.name}
                  </h3>

                  {/* 3 Specs: Seats, Transmission, Bags */}
                  <div className="flex items-center gap-3 mt-3 text-[11px] font-medium text-slate-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5 text-slate-400" />
                      <span>{car.seats} {t('pop.seats')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Cog className="w-3.5 h-3.5 text-slate-400" />
                      <span>{car.transmission}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Luggage className="w-3.5 h-3.5 text-slate-400" />
                      <span>{car.bags} {t('pop.bags')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price & Book Now Row */}
              <div
                className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between"
                onClick={(e) => e.stopPropagation()}
              >
                <div onClick={() => onViewDetails(car)} className="cursor-pointer">
                  <span className="text-xl font-black text-[var(--color-primary)] tracking-tight">
                    {fmt(car.pricePerDay)}
                  </span>
                  <span className="text-xs text-slate-400 font-medium"> / day</span>
                  {equiv(car.pricePerDay) ? (
                    <div className="text-[11px] text-slate-400">{equiv(car.pricePerDay)}</div>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => onBookCar(car)}
                  className="px-4 py-2 border border-slate-300 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white text-slate-800 text-xs font-bold tracking-wider uppercase rounded-lg transition-all cursor-pointer whitespace-nowrap active:scale-95"
                >
                  {t('pop.bookNow')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
